import { useState } from 'react';
import type { CustomerManagerUsageDailyByDayModelRow } from '@nadoutong/contracts';
import { TREND_TONE_CYCLE } from '../../components/charts/TrendChart.tsx';
import type { TrendTone } from '../../components/charts/TrendChart.tsx';
import { formatCompact, formatUsdMicrosOrCentsAsCnyDisplay } from '../../components/formatters.ts';
import { useT } from '../../i18n/index.ts';
import type { I18nKey } from '../../i18n/index.ts';
import { Button, Panel, TBody, THead, Table, Td, Th, Tr } from '../../ui/index.ts';
import { cx } from '../../ui/cx.ts';
import {
  breakdownByModel,
  breakdownBySku,
  shareValue,
  type UsageShareMetric,
  type UsageShareRow,
} from './usageMetrics.ts';

const TONE_BG: Record<TrendTone, string> = {
  signal: 'bg-signal',
  ok: 'bg-ok',
  warn: 'bg-warn',
  bad: 'bg-bad',
  paper: 'bg-paper',
  'signal-deep': 'bg-signal-deep',
  'bad-bright': 'bg-bad-bright',
  neutral: 'bg-paper-mid',
};

const TOKEN_FORMATTER = new Intl.NumberFormat('en-US');
const SHARE_METRIC_KEYS: Record<UsageShareMetric, I18nKey> = {
  spend: 'customer_manager_360.usage.share.by_spend',
  requests: 'customer_manager_360.usage.share.by_requests',
  tokens: 'customer_manager_360.usage.share.by_tokens',
};

export function UsageSharePanel({
  kind,
  rows,
  cnyPerUsd,
}: {
  readonly kind: 'model' | 'sku';
  readonly rows: readonly CustomerManagerUsageDailyByDayModelRow[];
  readonly cnyPerUsd: number | null;
}): JSX.Element {
  const t = useT();
  const [metric, setMetric] = useState<UsageShareMetric>('spend');
  const breakdown = kind === 'model' ? breakdownByModel(rows, metric) : breakdownBySku(rows, metric);
  const displayRows: readonly UsageShareRow[] =
    breakdown.other === null
      ? breakdown.rows
      : [
          ...breakdown.rows,
          {
            ...breakdown.other,
            label: t('customer_manager_360.usage.share.other', {
              count: breakdown.otherCount,
            }),
          },
        ];
  const total = breakdown.total;
  const testId = kind === 'model' ? 'customer-360-usage-models' : 'customer-360-usage-skus';

  return (
    <Panel
      caption={
        kind === 'model'
          ? t('customer_manager_360.usage.models.title')
          : t('customer_manager_360.usage.skus.title')
      }
      count={displayRows.length}
      padded
      testId={testId}
      actions={
        <div role="tablist" className="inline-flex gap-1">
          {(['spend', 'requests', 'tokens'] as const).map((option) => {
            const active = option === metric;
            return (
              <Button
                key={option}
                role="tab"
                aria-selected={active}
                variant={active ? 'secondary' : 'ghost'}
                size="sm"
                className={active ? 'bg-active text-paper' : undefined}
                data-testid={`${testId}-metric-${option}`}
                onClick={() => {
                  setMetric(option);
                }}
              >
                {t(SHARE_METRIC_KEYS[option])}
              </Button>
            );
          })}
        </div>
      }
    >
      {displayRows.length === 0 || total <= 0 ? (
        <p className="m-0 text-body text-paper-low">{t('customer_manager_360.usage.share.empty')}</p>
      ) : (
        <>
          <div className="mb-3 flex h-3 overflow-hidden rounded-sm">
            {displayRows.map((row, index) => {
              const tone = TREND_TONE_CYCLE[index % TREND_TONE_CYCLE.length] ?? 'neutral';
              const width = (shareValue(row, metric) / total) * 100;
              return (
                <span
                  key={row.key}
                  className={TONE_BG[tone]}
                  style={{ width: `${width.toFixed(2)}%` }}
                />
              );
            })}
          </div>
          <div className="mb-3 flex flex-wrap gap-2.5">
            {displayRows.map((row, index) => {
              const tone = TREND_TONE_CYCLE[index % TREND_TONE_CYCLE.length] ?? 'neutral';
              const pct = ((shareValue(row, metric) / total) * 100).toFixed(1);
              return (
                <span key={row.key} className="inline-flex items-center gap-1.5 text-micro text-paper-mid">
                  <span aria-hidden="true" className={cx('h-2 w-2 rounded-sm', TONE_BG[tone])} />
                  {row.label} {pct}%
                </span>
              );
            })}
          </div>
          <Table>
            <THead>
              <Tr interactive={false}>
                <Th>
                  {kind === 'model'
                    ? t('customer_manager_360.usage.share.model')
                    : t('customer_manager_360.usage.share.sku')}
                </Th>
                <Th num>{t('customer_manager_360.usage.share.requests')}</Th>
                <Th num>{t('customer_manager_360.usage.share.tokens')}</Th>
                <Th num>{t('customer_manager_360.usage.share.spend')}</Th>
                <Th num>{t('customer_manager_360.usage.share.share')}</Th>
              </Tr>
            </THead>
            <TBody>
              {displayRows.map((row, index) => {
                const tone = TREND_TONE_CYCLE[index % TREND_TONE_CYCLE.length] ?? 'neutral';
                const pct = total > 0 ? ((shareValue(row, metric) / total) * 100).toFixed(1) : '0.0';
                return (
                  <Tr key={row.key} interactive={false}>
                    <Td>
                      <span className="inline-flex items-center gap-2">
                        <span aria-hidden="true" className={cx('h-2 w-2 rounded-sm', TONE_BG[tone])} />
                        {row.label}
                        {row.secondary !== null ? (
                          <span className="text-paper-low">{row.secondary}</span>
                        ) : null}
                      </span>
                    </Td>
                    <Td num>{TOKEN_FORMATTER.format(row.request_count)}</Td>
                    <Td num>
                      {formatCompact(row.input_tokens)} / {formatCompact(row.output_tokens)}
                    </Td>
                    <Td num>
                      {formatUsdMicrosOrCentsAsCnyDisplay(
                        row.usd_charged_micros,
                        row.usd_charged_cents,
                        cnyPerUsd,
                        false,
                      )}
                    </Td>
                    <Td num>{pct}%</Td>
                  </Tr>
                );
              })}
            </TBody>
          </Table>
        </>
      )}
    </Panel>
  );
}
